import { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, Flame } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import api from '../../../services/api';
import { toast } from 'sonner';
import './halloween-entry.css';

const showComingSoon = () => toast.info('The offers will be live soon.', {
  id: 'halloween-offers-coming-soon', position: 'top-center', duration: 4000,
});

export default function HalloweenEntryCard({ onEnter = showComingSoon }) {
  const [live, setLive] = useState(false);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const { data } = await api.get('/api/halloween/availability', { timeout: 10000 });
        if (active) setLive(data.active === true);
      } catch { if (active) setLive(false); }
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => { active = false; window.removeEventListener('focus', refresh); };
  }, []);
  const enter = () => live ? navigate(tenantSlug ? `/${tenantSlug}/app/halloween` : '/app/halloween') : onEnter();
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  const [inView, setInView] = useState(true);
  const [resting, setResting] = useState(false);
  const [wave, setWave] = useState(0);
  const restRemaining = useRef(10000);
  const root = useRef(null);
  const lightFilter = `halloween-light-${useId().replace(/:/g, '')}`;
  const dimFilter = `${lightFilter}-dim`;
  useEffect(() => {
    const sync = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return undefined;
    const sync = () => setReducedMotion(media.matches);
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    if (root.current) observer.observe(root.current.querySelector('.halloween-entry__scene') || root.current);
    return () => observer.disconnect();
  }, []);
  const motionActive = !reducedMotion && !hidden && inView;
  useEffect(() => {
    if (!resting || !motionActive) return undefined;
    const started = Date.now();
    let finished = false;
    const timer = setTimeout(() => {
      finished = true;
      restRemaining.current = 10000;
      setResting(false);
      setWave(value => value + 1);
    }, restRemaining.current);
    return () => {
      clearTimeout(timer);
      if (!finished) restRemaining.current = Math.max(0, restRemaining.current - (Date.now() - started));
    };
  }, [resting, motionActive]);
  const finishFlock = event => {
    if (event.target === event.currentTarget && event.animationName === 'halloween-fly') {
      restRemaining.current = 10000;
      setResting(true);
    }
  };
  const bats = ['a', 'b', 'c', 'a', 'b'];
  return (
    <section ref={root} className={`halloween-entry${live ? ' halloween-entry--live' : ''}${motionActive ? '' : ' halloween-entry--paused'}${resting ? ' halloween-entry--resting' : ''}`} data-testid="halloween-entry-card" aria-label="Halloween offers">
      <div className="halloween-entry__scene">
        <svg className="halloween-entry__filter" aria-hidden="true" focusable="false">
          <defs><filter id={dimFilter} colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  .5 .5 0 0 -.32" />
            <feComponentTransfer><feFuncA type="linear" slope="5" /></feComponentTransfer>
            <feGaussianBlur stdDeviation=".65" />
          </filter><filter id={lightFilter} colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 .55  0 0 0 0 .12  .5 .5 0 0 -.46" />
            <feComponentTransfer><feFuncA type="linear" slope="6" /></feComponentTransfer>
            <feGaussianBlur stdDeviation="2.5" />
          </filter></defs>
        </svg>
        <img className="halloween-entry__background" src="/halloween-preview/entrance.png" alt="" />
        <img className="halloween-entry__dimlight" src="/halloween-preview/entrance.png" alt="" aria-hidden="true" style={{ filter: `url(#${dimFilter})` }} />
        <img className="halloween-entry__glow" src="/halloween-preview/entrance.png" alt="" aria-hidden="true" style={{ filter: `url(#${lightFilter})` }} />
        <div className="halloween-entry__shade" aria-hidden="true" />
        {live && <div className="halloween-entry__live" aria-label="Live now" data-testid="halloween-live-badge"><Flame size={22} fill="#ff8a30" stroke="#ffd98b" aria-hidden="true" /><span>LIVE NOW</span></div>}
        {bats.map((phase, index) => <span key={`${wave}-${index}`} className={`halloween-entry__flight halloween-entry__flight--${index}`} onAnimationEnd={index === bats.length - 1 ? finishFlock : undefined} aria-hidden="true"><img src={motionActive && !resting ? `/halloween-preview/bat-flap-v2-${phase}.gif` : '/halloween-preview/bat-flap-v2-static.png'} alt="" /></span>)}
        <div className="halloween-entry__content"><h2>Halloween<span>Offers</span></h2><button type="button" className="halloween-entry__enter" onClick={enter}>Enter Now<ArrowRight size={18} aria-hidden="true" /></button></div>
      </div>
    </section>
  );
}
